import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useChatsOwner from '../../hooks/useChatsOwner';
import { dropDoctorCaches } from '../../lib/doctorKeys';
import { doctorSwitches, DOCTOR_TAG } from '../../lib/doctorSwitch';
import { SLOTS_ROOT } from '../ChatsCacheGuard';

/*
 * r16sd review MF2 (2026-10-27): App.js keeps ONE QueryClient for the tab and signing out / in is an SPA navigate. When the signed-in
 * account changes (sign-out, or another account signing in in the same tab) this drops every account's Settings Doctor data:
 *   - ['me', 'doctor', ...]: the credits and the account banners (hooks/useDoctorMe.js);
 *   - ['doctor', ...]: every slot's doctor panel (the drawer, the settings page header, the Follow-back note);
 *   - and RESETS the shared ['slots'] list, whose rows carry the doctor's holds[] (the hold texts) and attention.
 * The keys already carry the account (lib/doctorKeys.js), so another account never READS them; this makes them GONE. It runs
 * whatever the r16p switch says (ChatsCacheGuard does the chat caches only while r16p is on). With the doctor's master switch off
 * (necro.ui.r16sd=off) no doctor query exists and nothing is drawn from the rows' holds, so nothing runs: r16pd exactly.
 * The first sign-in of a page load drops nothing. Renders nothing. Mounted once in App, inside the providers.
 */
export default function DoctorCacheGuard() {
  const queryClient = useQueryClient();
  const owner = useChatsOwner();
  const last = useRef(owner);
  useEffect(() => {
    const before = last.current;
    last.current = owner;
    if (before === owner || before === 'signed-out' || !doctorSwitches().master) return;
    dropDoctorCaches(queryClient);
    queryClient.resetQueries({ queryKey: SLOTS_ROOT });
    // eslint-disable-next-line no-console
    console.info(`${DOCTOR_TAG} account changed: doctor caches dropped, slot rows reset (${owner === 'signed-out' ? 'signed out' : 'another account'})`);
  }, [owner, queryClient]);
  return null;
}
